import { useState, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	LinearProgress,
	CircularProgress,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Button,
} from "@mui/material";
import { DateFilter } from "./components/DateFilter";

import { Avatar } from "../../components/fieldsViews/fields/Avatar";
import { useRecords } from "../../api/useRecords";
import { useCreateRecord } from "../../api/useCreateRecord";
import { useQueryClient } from "@tanstack/react-query";
import { FileField } from "../../components/fieldsViews/fields/FileField";
import { useField } from "../../api/useField";
import { SalariesUploadButton } from "./components/SalariesUploadButton";
import { ExportExcel } from "../../components/export/ExportExcel";
import { DoneAll, OpenInNew, Error } from "@mui/icons-material";
import { Announcement } from "@mui/icons-material";
import { endOfMonth, parseISO, startOfMonth, format } from "date-fns";
import {
	calculateHoursTotals,
	calculateTimeOffsSummary,
	countWorkDaysOfAtLeastFiveHours,
	computeCumulativeHours,
	getCumulativeToDate,
	getEffectiveCumulativeToDate,
	hasValidDayOffConfig,
	buildClinicHolidaySet,
	resolveClinicId,
} from "./utils/calculations";
import {
	getCurrentRun,
	getCurrentRunBounds,
	getMonthRunReferenceDate,
	getVacationRun,
	filterTimeOffForBag,
	hasUnknownAgreementType,
} from "./utils/employmentRun";
import {
	getMixedContractMonthWarning,
	getMonthContractTypes,
	isMixedContractMonth,
	isNonContractMonth,
	isServiceProviderMonth,
} from "./utils/monthContractType";
import { useAgreementTypeLabels } from "./utils/useAgreementTypeLabels";
import { getExportData } from "./utils/exportData";
import { useStaffWorkLogsFilter, applyEmployeeFilters } from "./utils/filters";
import { useWorkLogs } from "./utils/useWorkLogs";
import { sortBy } from "lodash";
import { renderFieldView } from "../../components/fieldsViews/renderFieldView";
import { BonusChip } from "./components/BonusChip";
import { usePortugalHolidaysRange } from "../../api/usePortugalHolidays";
import { MissedWorkIcons } from "./components/MissedWorkIcons";
import { isRoleDirector, isRoleMarketingManager } from "../../utils/rolesUtils";
import { SalariesReceiptsSendEmailButton } from "./components/SalariesReceiptsSendEmailButton";
import { getCumulativeStartDate, getExtraHoursPaidStart } from "./utils/constants";
import { resolveRunBagWindow, getYearToFreeze, computeEmployeeSnapshotPayload } from "./utils/snapshots";
import { CumulativeBalanceSummary } from "./components/CumulativeBalanceSummary";
import { computeTimeOffHolidaysSummary, calculateHolidayHoursCredited } from "./utils/holidaysSummary";
import { useHolidayHistoryByUser } from "./utils/useHolidayHistoryByUser";
import { HolidaysSummaryCell } from "./components/HolidaysSummaryCell";
import { ProfessionalCategoryFilter } from "./components/ProfessionalCategoryFilter";
import {
	calculateServiceProviderPay,
	getAgreementRemunerations,
	groupRemunerationsByAgreement,
	isReceiptDifferentFromPay,
	STAFF_REMUNERATION_FIELDS,
} from "./utils/serviceProviderPay";
import { formatCurrency } from "../../utils/formatCurrency";
import store2 from "store2";
import { CollectionsContext } from "../../components/root/CollectionsProvider";
import { useStaffPayHours } from "./utils/useStaffPayHours";
import { resolveHoursRows } from "./utils/payDataSources";

export const StaffWorkLogsList = () => {
	const { formatedField: documentField } = useField("staff_monthly_payments", "files");
	const { formatedField: professionalCategoryField } = useField("staff_agreements", "professional_category");

	const [selectedAgreementType, setSelectedAgreementType] = useState<string>("all");
	const [selectedJob, setSelectedJob] = useState<Array<string>>([]);
	const agreementTypeLabels = useAgreementTypeLabels();
	const { selectedYear, setSelectedYear, selectedMonth, setSelectedMonth } = useStaffWorkLogsFilter();
	const selectedMonthDate = new Date(selectedYear, selectedMonth);

	const startDate = startOfMonth(selectedMonthDate);
	const endDate = endOfMonth(selectedMonthDate);

	const { workLogs, timeOff, isLoading, isError, isCurrentUserAdmin, currentUser, staffAgreements, allowedUserIds, snapshotsByUser } = useWorkLogs(startDate, endDate);

	const createSnapshot = useCreateRecord();
	const queryClient = useQueryClient();
	const allSnapshots = useMemo(() => Array.from(snapshotsByUser.values()).flat(), [snapshotsByUser]);

	const cumulativeStartDate = getCumulativeStartDate();
	const { year: extraHoursPaidStartYear, month: extraHoursPaidStartMonth } = getExtraHoursPaidStart();
	const currentYear = new Date().getFullYear();
	const holidayStartYear = cumulativeStartDate.getFullYear();

	const { data: allHolidays, isLoading: holidaysLoading } = usePortugalHolidaysRange(holidayStartYear, currentYear);

	const hasFinanceAccess = Boolean(store2.get("finance_auth"));
	const collections = useContext(CollectionsContext);
	const canReadFinancePayments =
		hasFinanceAccess && Boolean(collections?.collections.some((collection) => collection.collection === "staff_monthly_payments"));

	const paymentsQuery = useRecords("staff_monthly_payments", "*.*", {
		extraOptions: {
			filter: { _and: [{ year: { _eq: selectedYear } }, { month: { _eq: selectedMonth + 1 } }] },
		},
		enabled: canReadFinancePayments,
	});
	const payments = paymentsQuery.data;

	const allExtraHoursPaymentsQuery = useRecords(
		"staff_monthly_payments", "operations_user_id,extra_hours_paid,year,month", {
			extraOptions: {
				filter: {
					_and: [
						{ extra_hours_paid: { _nnull: true } },
						{ _or: [
							{ year: { _gt: extraHoursPaidStartYear } },
							{ _and: [{ year: { _eq: extraHoursPaidStartYear } }, { month: { _gte: extraHoursPaidStartMonth } }] },
						]},
					],
				},
				pageSize: -1,
			},
			enabled: canReadFinancePayments,
		}
	);

	const listedUserIds = useMemo(
		() => staffAgreements.map((agreement: any) => agreement.user?.id).filter(Boolean),
		[staffAgreements]
	);
	const staffPayHoursQuery = useStaffPayHours({
		viewerId: currentUser?.id,
		userIds: listedUserIds,
		selectedYear,
		selectedMonth,
		enabled: !hasFinanceAccess,
	});
	const hoursRows = resolveHoursRows({
		hasFinanceAccess,
		canReadFinancePayments,
		hasPeopleToAsk: listedUserIds.length > 0,
		financeQueries: [paymentsQuery, allExtraHoursPaymentsQuery],
		financeMonthRows: paymentsQuery.data?.items ?? [],
		financeExtraHoursPaidRows: allExtraHoursPaymentsQuery.data?.items ?? [],
		staffPayHoursQuery,
	});
	const isHoursDataReady = hoursRows.availability.state === "ready";

	const extraHoursPaymentsByUser = useMemo(() => {
		const map = new Map<string, Array<any>>();
		hoursRows.extraHoursPaidRows.forEach((p: any) => {
			const current = map.get(p.operations_user_id) || [];
			current.push(p);
			map.set(p.operations_user_id, current);
		});
		return map;
	}, [hoursRows.extraHoursPaidRows]);

	const monthCorrectionByUser = useMemo(() => {
		const correctionByUser = new Map<string, number>();
		hoursRows.monthRows.forEach((monthRow) => {
			correctionByUser.set(monthRow.operations_user_id, monthRow.expected_hours_correction || 0);
		});
		return correctionByUser;
	}, [hoursRows.monthRows]);

	const holidayHistoryByUser = useHolidayHistoryByUser(selectedYear);

	const listedAgreementIds = useMemo(() => staffAgreements.map((agreement) => String(agreement.id)), [staffAgreements]);
	const { data: remunerations, status: remunerationsStatus } = useRecords("staff_remuneration", STAFF_REMUNERATION_FIELDS, {
		extraOptions: {
			filter: { operations_staff_agreement_id: { _in: listedAgreementIds } },
			pageSize: -1,
		},
		enabled: hasFinanceAccess && listedAgreementIds.length > 0,
	});
	const remunerationsByAgreement = useMemo(() => groupRemunerationsByAgreement(remunerations?.items || []), [remunerations]);

	const employeeSummaries = useMemo(() => {
		if (isLoading || isError) return [];

		const summariesMap = new Map();

		workLogs.forEach((log: any) => {
			if (log.user && log.user.id) {
				if (!summariesMap.has(log.user.id)) {
					summariesMap.set(log.user.id, {
						user: log.user,
						workLogs: [],
						timeOffs: [],
						staffAgreement: {}

					});
				}
				summariesMap.get(log.user.id).workLogs.push(log);
			}
		});

		timeOff.forEach((to: any) => {
			if (!summariesMap.has(to.user.id)) {
				summariesMap.set(to.user.id, {
					user: to.user,
					workLogs: [],
					timeOffs: [],
					staffAgreement: {}
				});
			}
			summariesMap.get(to.user.id).timeOffs.push(to);
		});
		payments?.items?.forEach((payment: any) => {
			if (summariesMap.has(payment.operations_user_id)) {
				const summary = summariesMap.get(payment.operations_user_id);
				summary.payment = {
					...payment,
				};
			}
		});
		staffAgreements.forEach((item: any) => {
			const { user, ...restStaffAgreement } = item;
			if (!user?.id) return;
			const technicianSpecializations: Array<string> = user?.technician_id?.flatMap(
				(technician: any) => technician?.specializations ?? []
			) ?? [];
			if (summariesMap.has(user?.id)) {
				const existing = summariesMap.get(user?.id);
				existing.staffAgreement = restStaffAgreement;
				existing.technicianSpecializations = technicianSpecializations;
			}
			else if (!summariesMap.has(user?.id) && restStaffAgreement.status === "active") {
				summariesMap.set(user?.id, {
					user: user,
					workLogs: [],
					timeOffs: [],
					staffAgreement: restStaffAgreement,
					technicianSpecializations,
				});
			}
		})


		return Array.from(summariesMap.values()).map((summary) => {
			const sortedWeeklyHours = sortBy(summary.staffAgreement?.staff_weekly_hours || [], (weeklyHour: any) => weeklyHour?.date_start);
			const runReferenceDate = getMonthRunReferenceDate(selectedMonthDate);
			const monthRunRows = getCurrentRun(sortedWeeklyHours, runReferenceDate)?.rows ?? [];
			const cumulativeToDate = getEffectiveCumulativeToDate(selectedMonthDate, monthRunRows);
			const currentRunBounds = getCurrentRunBounds(sortedWeeklyHours, runReferenceDate);
			const agreementTypeMissing = sortedWeeklyHours.length === 0 || hasUnknownAgreementType(sortedWeeklyHours);
			const monthContractTypes = getMonthContractTypes({
				weeklyHoursSchedule: sortedWeeklyHours,
				agreementType: summary.staffAgreement?.agreement_type,
				monthDate: selectedMonthDate,
			});

			const totalsWorkHours = calculateHoursTotals(summary.workLogs, { capToMonth: selectedMonthDate, ...currentRunBounds });

			const userCorrection = monthCorrectionByUser.get(summary.user?.id) ?? 0;

			const publicHolidaysSet = buildClinicHolidaySet(allHolidays, resolveClinicId(summary.staffAgreement?.default_clinic));
			const userSnapshots = snapshotsByUser.get(summary.user?.id) || [];
			const userExtraHoursPayments = extraHoursPaymentsByUser.get(summary.user?.id) || [];
			const monthBagWindow = resolveRunBagWindow({
				weeklyHoursSchedule: sortedWeeklyHours,
				snapshots: userSnapshots,
				extraHoursPayments: userExtraHoursPayments,
				referenceDate: runReferenceDate,
			});
			// The year freeze settles today's relationship, whatever month is on screen.
			const freezeBagWindow = resolveRunBagWindow({
				weeklyHoursSchedule: sortedWeeklyHours,
				snapshots: userSnapshots,
				extraHoursPayments: userExtraHoursPayments,
				referenceDate: new Date(),
			});
			const freezeDayOffConfigMissing = !hasValidDayOffConfig(
				sortedWeeklyHours,
				freezeBagWindow.cumulativeFromDate,
				getEffectiveCumulativeToDate(selectedMonthDate, sortedWeeklyHours),
			);
			const dayOffConfigMissing = !hasValidDayOffConfig(sortedWeeklyHours, monthBagWindow.cumulativeFromDate, cumulativeToDate);

			const bagTimeOffs = filterTimeOffForBag(summary.timeOffs, sortedWeeklyHours);

			const { cumulativeExpected, cumulativeWorked } = !dayOffConfigMissing && !agreementTypeMissing
				? computeCumulativeHours({
					workLogs: summary.workLogs,
					timeOffs: bagTimeOffs,
					toDate: cumulativeToDate,
					weeklyWorkHoursSchedule: sortedWeeklyHours,
					publicHolidaysSet,
					expectedHoursCorrection: userCorrection,
					extraHoursPaid: monthBagWindow.extraHoursPaid,
					cumulativeStartDate: monthBagWindow.windowStart,
					employmentStartDate: monthBagWindow.runStartDate,
					priorBalance: monthBagWindow.priorBalance,
				})
				: { cumulativeExpected: NaN, cumulativeWorked: NaN };

			const timeOffsSummary = calculateTimeOffsSummary(startDate, endDate, summary.timeOffs, allHolidays);

			const mealAllowanceDays = countWorkDaysOfAtLeastFiveHours(summary.workLogs, { capToMonth: selectedMonthDate, ...currentRunBounds });

			const serviceProviderPay = calculateServiceProviderPay({
				workLogs: summary.workLogs,
				remunerations: getAgreementRemunerations(remunerationsByAgreement, summary.staffAgreement?.id),
				monthDate: selectedMonthDate,
				weeklyHoursSchedule: sortedWeeklyHours,
			});

			const userHolidayHistory = holidayHistoryByUser.get(summary.user?.id);
			const thisYearHolidayItems = userHolidayHistory?.thisYear || [];
			const vacationFromFirstRow = Boolean(summary.staffAgreement?.vacation_from_first_row);
			const holidaysSummary = computeTimeOffHolidaysSummary({
				selectedYear,
				priorYearsHolidayItems: userHolidayHistory?.priorYears,
				unusedVacationDaysAt31Dec2025: summary.staffAgreement?.unused_vacation_days_at_31_dec_2025,
				thisYearHolidayItems,
				weeklyHoursSchedule: sortedWeeklyHours,
				vacationFromFirstRow,
			});

			const vacationRun = agreementTypeMissing ? null : getVacationRun(sortedWeeklyHours, vacationFromFirstRow);
			const selectedMonthPrefix = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
			const monthHolidayItems = thisYearHolidayItems.filter((entry: any) => entry.start_date.startsWith(selectedMonthPrefix));
			const monthHolidayHours = vacationRun
				? calculateHolidayHoursCredited(monthHolidayItems, vacationRun.rows, vacationRun.endDate, vacationRun.startDate)
				: 0;

			return {
				user: {
					...summary.user,
					technicianSpecializations: summary.technicianSpecializations ?? [],
				},
				totalWorkHours: cumulativeWorked,
				expectedHours: cumulativeExpected,
				totalClientHours: totalsWorkHours.totalClientHours,
				effectiveWorkHours: totalsWorkHours.effectiveWorkHours,
				totalHolidays: timeOffsSummary.daysHolidayCurrentMonthTotal,
				totalSickLeaveDays: timeOffsSummary.daysSickLeaveCurrentMonthTotal,
				payment: summary.payment || null,
				mealAllowanceDays: mealAllowanceDays,
				staffAgreement: summary.staffAgreement,
				hasMissingHours: timeOffsSummary.hoursMissedCurrentMonthTotal > 0,
				holidaysSummary,
				monthHolidayHours,
				dayOffConfigMissing,
				agreementTypeMissing,
				priorBalance: monthBagWindow.priorBalance,
				monthContractTypes,
				serviceProviderPay,
				// A snapshot freezes a balance permanently — never freeze while the schedule is untyped.
				freezeInputs: !freezeDayOffConfigMissing && !agreementTypeMissing && summary.staffAgreement?.agreement_type !== "services_provider"
					? {
						user: summary.user?.id,
						workLogs: summary.workLogs,
						timeOffs: bagTimeOffs,
						weeklyWorkHoursSchedule: sortedWeeklyHours,
						publicHolidaysSet,
						expectedHoursCorrection: userCorrection,
						extraHoursPaid: freezeBagWindow.extraHoursPaid,
						employmentStartDate: freezeBagWindow.runStartDate,
						currentRunStartDate: freezeBagWindow.runStartDate,
					}
					: null,
			};
		});
	}, [workLogs, timeOff, payments, isLoading, isError, allHolidays, staffAgreements, selectedMonthDate, startDate, endDate, extraHoursPaymentsByUser, monthCorrectionByUser, holidayHistoryByUser, selectedYear, selectedMonth, remunerationsByAgreement]);

	const isDirector = isRoleDirector(currentUser?.role?.id)
	const isMarketingManager = isRoleMarketingManager(currentUser?.role?.id)

	const filteredEmployeeSummaries = useMemo(() => {
		const sortedEmployeeSummaries = sortBy(employeeSummaries, (summary) => summary.user.first_name);

		const visibleSummaries = isCurrentUserAdmin || isDirector
			? sortedEmployeeSummaries
			: sortedEmployeeSummaries.filter((summary) => allowedUserIds.has(summary.user.id))

		return applyEmployeeFilters(visibleSummaries, selectedAgreementType, selectedJob);
	}, [employeeSummaries, selectedAgreementType, selectedJob, currentUser, allowedUserIds]);

	if (isLoading || holidaysLoading) {
		return <LinearProgress color="secondary" />;
	}

	if (!isCurrentUserAdmin && !isDirector && !isMarketingManager) {
		return <Typography variant="h6">Você não tem permissão para ver esta página</Typography>;
	}

	if (isError) {
		return <Typography>Error loading data</Typography>;
	}

	const freezeCheckpoint = getYearToFreeze(new Date(), allSnapshots);

	const handleFreezeYear = async () => {
		if (!freezeCheckpoint) return;
		const checkpointYear = freezeCheckpoint.getFullYear();
		for (const summary of employeeSummaries) {
			const inputs = summary.freezeInputs;
			if (!inputs) continue;
			const existingSnapshots = snapshotsByUser.get(inputs.user) || [];
			const alreadyFrozen = existingSnapshots.some(
				(snapshot) => parseISO(snapshot.checkpoint_date).getFullYear() === checkpointYear
			);
			if (alreadyFrozen) continue;
			const payload = computeEmployeeSnapshotPayload({ ...inputs, checkpoint: freezeCheckpoint, existingSnapshots });
			await createSnapshot.mutateAsync({ entity: "staff_hours_snapshots", data: payload });
		}
		await queryClient.invalidateQueries({
			predicate: (query: any) => query.queryKey[0]?.includes?.("staff_hours_snapshots"),
		});
	};

	return (
		<Box>
			<Paper sx={{ p: 2, mb: 2, display: "flex", justifyContent: "space-between", gap: 4, alignItems: "center" }}>
				<DateFilter
					selectedYear={selectedYear}
					selectedMonth={selectedMonth}
					setSelectedYear={setSelectedYear}
					setSelectedMonth={setSelectedMonth}
				/>
				<FormControl fullWidth>
					<InputLabel id="agreement-type-select-label">Tipo de Acordo</InputLabel>
					<Select
						labelId="agreement-type-select-label"
						value={selectedAgreementType}
						onChange={(e) => setSelectedAgreementType(e.target.value as string)}
						fullWidth
						label="Tipo de Acordo"
					>
						<MenuItem value="all">Todos</MenuItem>
						<MenuItem value="services_provider">Recibos Verdes</MenuItem>
						<MenuItem value="contracts">Contratos</MenuItem>
					</Select>
				</FormControl>
				<ProfessionalCategoryFilter
					selected={selectedJob}
					onChange={setSelectedJob}
				/>
				{isCurrentUserAdmin && (
					<>
						<SalariesUploadButton
							selectedYear={selectedYear}
							selectedMonth={selectedMonth}
							type="salaries"
							buttonText="Enviar salários"
						/>

						<SalariesReceiptsSendEmailButton
							selectedYear={selectedYear}
							selectedMonth={selectedMonth}
							buttonText="Enviar recibos"
						/>

						{hasFinanceAccess && isHoursDataReady && (
							<>
								<ExportExcel
									getData={() => getExportData(employeeSummaries, "salaries")}
									fileName={`salaries_${selectedYear}_${selectedMonth + 1}`}
									buttonText="Exportar Banco"
									includeHeaders={false}
									format="txt"
								/>
								<ExportExcel
									getData={() => getExportData(employeeSummaries, "subsidies")}
									fileName={`subsidy_${selectedYear}_${selectedMonth + 1}`}
									buttonText="Exportar Ticket"
									includeHeaders={true}
								/>
							</>
						)}
					</>
				)}
			</Paper>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell align="center">Detalhe</TableCell>
							<TableCell align="left">Colaborador</TableCell>
							<TableCell align="left">Cat. Profissional</TableCell>
							{isCurrentUserAdmin || isDirector ? <TableCell align="center">Pag. Bónus</TableCell> : null}
							<TableCell align="center">Dias de Subsídio</TableCell>
							<TableCell align="center">
								Horas Trabalhadas
								<Typography variant="caption" display="block" color="text.secondary">
									Saldo até {format(getCumulativeToDate(selectedMonthDate), "dd/MM/yyyy")}
								</Typography>
							</TableCell>
							<TableCell align="center">Férias</TableCell>
							<TableCell align="center">Horas Clientes</TableCell>
							<TableCell align="center">Baixa</TableCell>
							<TableCell align="center">Validação</TableCell>
							<TableCell align="center">Notas</TableCell>
							{documentField && <TableCell align="center">Documentos</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredEmployeeSummaries.map((summary: any) => {
							const isNonContract = isNonContractMonth(summary.monthContractTypes);
							const isMixedMonth = isMixedContractMonth(summary.monthContractTypes);
							const mixedMonthWarning = isMixedMonth && (
								<Tooltip
									title={getMixedContractMonthWarning({
										weeklyHoursSchedule: summary.staffAgreement?.staff_weekly_hours || [],
										monthDate: selectedMonthDate,
										agreementTypeLabels,
									})}
									arrow
								>
									<Error color="error" fontSize="small" />
								</Tooltip>
							);
							const paymentGross = summary?.payment?.gross;
							const showsReceiptWarning =
								remunerationsStatus === "success" &&
								isServiceProviderMonth(summary.monthContractTypes) &&
								Boolean(paymentGross) &&
								(summary.serviceProviderPay.hasConflict ||
									isReceiptDifferentFromPay(paymentGross, summary.serviceProviderPay.totalValue));
							const receiptWarningText = summary.serviceProviderPay.hasConflict
								? `Recibo ${formatCurrency(paymentGross)} · Remuneração em conflito`
								: `Recibo ${formatCurrency(paymentGross)} · Calculado ${formatCurrency(summary.serviceProviderPay.totalValue)}`;
							const hasDocuments = summary?.payment?.files?.length > 0;
							const staffAgreement = summary?.staffAgreement;

							const hideHoursDifference =
								!summary.user.role || isRoleDirector(summary.user.role);

							return (
								<TableRow key={summary.user.id}>
									<TableCell align="center">
										<IconButton
											sx={{ p: 0 }}
											onClick={(e) => {
												e.stopPropagation();
											}}
											color="secondary"
											component={Link}
											to={`/staff-work-logs/${summary.user.id}?year=${selectedYear}&month=${selectedMonth + 1
												}`}
											target="_blank"
										>
											<OpenInNew />
										</IconButton>
									</TableCell>
									<TableCell align="left">
										<Box
											component={Link}
											to={`/staff-work-logs/${summary.user.id}`}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 4,
												textDecoration: "none",
												color: "inherit",
											}}
										>
											<Avatar value={summary.user.avatar} />
											<Box>
												<Typography>{summary.user.first_name}</Typography>
												<Typography>{summary.user.last_name}</Typography>
											</Box>
										</Box>
									</TableCell>
									<TableCell align="left" sx={{ width: 80 }}>
										{professionalCategoryField &&
											renderFieldView(
												professionalCategoryField,
												{ professional_category: staffAgreement?.professional_category },
												"group"
											)}
									</TableCell>
									{isCurrentUserAdmin || isDirector ? (
										<TableCell align="center" sx={{ width: 80 }}>
											<BonusChip staffAgreement={staffAgreement} />
										</TableCell>
									) : null}
									<TableCell align="center">
										{isNonContract ? "" : isMixedMonth ? mixedMonthWarning : summary.mealAllowanceDays}
									</TableCell>
									<TableCell align="center">
										{!isNonContract &&
											(isMixedMonth ? (
												mixedMonthWarning
											) : summary.dayOffConfigMissing || summary.agreementTypeMissing ? (
												<Tooltip
													title={
														summary.agreementTypeMissing
															? "Tipo de contrato em falta — definir nas horas semanais"
															: "Tipo de folga em falta — definir nas horas semanais"
													}
													arrow
												>
													<Error color="error" fontSize="small" />
												</Tooltip>
											) : hideHoursDifference ? null : hoursRows.availability.state === "unavailable" ? (
												<Tooltip title={`Saldo indisponível: ${hoursRows.availability.reason}`} arrow>
													<Error color="error" fontSize="small" />
												</Tooltip>
											) : hoursRows.availability.state === "loading" ? (
												<Tooltip title="A carregar o saldo" arrow>
													<CircularProgress size={16} color="secondary" />
												</Tooltip>
											) : (
												<>
													<CumulativeBalanceSummary
														worked={summary.totalWorkHours}
														expected={summary.expectedHours}
														priorBalance={summary.priorBalance}
													/>
													<MissedWorkIcons hasMissingHours={summary.hasMissingHours} />
												</>
											))}
									</TableCell>
									<TableCell align="center">
										<HolidaysSummaryCell
											monthHolidayHours={summary.monthHolidayHours}
											holidaysSummary={summary.holidaysSummary}
										/>
									</TableCell>
									<TableCell align="center">{summary.totalClientHours?.toFixed(0)}</TableCell>
									<TableCell align="center">{summary.totalSickLeaveDays}</TableCell>
									<TableCell align="center">
										{summary.payment?.admin_validated ? <DoneAll color="success" /> : null}

										{showsReceiptWarning ? (
											<Tooltip title={receiptWarningText} arrow>
												<Error sx={{ ml: 1 }} color="error" />
											</Tooltip>
										) : null}
									</TableCell>
									<TableCell align="center">
										{summary.payment?.note && (
											<Tooltip title={summary.payment.note} placement="left" arrow>
												<IconButton color="warning">
													<Announcement />
												</IconButton>
											</Tooltip>
										)}
									</TableCell>

									<TableCell align="center">
										{isCurrentUserAdmin && documentField && hasDocuments && summary.payment?.id && (
											<FileField
												value={summary.payment?.files}
												field={documentField}
												fieldView="grid"
												entity="staff_monthly_payments"
												id={summary.payment?.id}
												extraOptions={{
													fileFolder: "a80a2485-67b2-4c60-98bb-4b5a61d8bd39",
													field: "files",
													instanceToUse: "finance",
												}}
											/>
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			{isCurrentUserAdmin && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 2 }}>
					<Button variant="outlined" color="primary" disabled={!freezeCheckpoint || !isHoursDataReady} onClick={handleFreezeYear}>
						Fechar ano
					</Button>
				</Box>
			)}
		</Box>
	);
};
